import { Title } from "../Text";
import { useTranslation } from "react-i18next";
import { Button } from "../ui/button";
import { useMemo } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { artworkSchema } from "@/api/artwork/artwork-validation";
import { appendNoDuplicateRecordingNumbersRefine } from "@/api/artwork/artwork-validation";
import {
  Form,
  FormField,
  FormControl,
  FormItem,
  FormLabel,
  FormMessage,
} from "../ui/form";
import { Input } from "../ui/input";
import { useCreateArtwork } from "@/api/artwork/artwork";
import { toast } from "@/hooks/use-toast";
import { useNavigate } from "react-router-dom";
import { isAxiosError } from "axios";
import { uploadImage } from "@/api/image";
import { Attachment } from "@/api/attachment";
import { generateImageName } from "@/lib/utils";
import { useAuth } from "@/context/auth/AuthContext";
import { useEmbedAttachment } from "@/api/artmatch";

const formSchema = appendNoDuplicateRecordingNumbersRefine(
  artworkSchema
    .pick({
      id: true,
      oeuvresDescriptionNumeroEnregistrement: true,
    })
    .extend({
      image: z.instanceof(File),
    }),
);

type FormData = z.infer<typeof formSchema>;

type ArtmatchCreateNewArtworkProps = {
  image: File;
  onCancel: () => void;
};

export const ArtmatchCreateNewArtwork = ({
  image,
  onCancel,
}: ArtmatchCreateNewArtworkProps) => {
  const { t } = useTranslation();
  const { mutateAsync: createArtwork } = useCreateArtwork();
  const { mutateAsync: embedAttachment } = useEmbedAttachment();
  const { instanceData } = useAuth();
  const navigate = useNavigate();

  const imageSrc = useMemo(() => URL.createObjectURL(image), [image]);

  const form = useForm<FormData>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      image,
      oeuvresDescriptionNumeroEnregistrement: "",
    },
    mode: "onSubmit",
    reValidateMode: "onSubmit",
  });

  const handleCancel = () => {
    URL.revokeObjectURL(imageSrc);
    onCancel();
  };

  const onSubmit = async (data: FormData) => {
    if (!instanceData) return;

    try {
      const result = await createArtwork(data);
      try {
        const attachment = await uploadImage(
          data.image,
          {
            ...data,
            published: true,
            position: "oeuvresPhotoFaceOeuvre",
            docId: result.id,
            details: "",
            name: generateImageName({
              primaryType: "artwork",
              artwork: result,
              translatedPosition: t("oeuvresPhotoFaceOeuvre"),
            }),
          } as Attachment,
          instanceData.s3Bucket,
          instanceData.id,
          "back/api/attachment/create",
        );
        embedAttachment(attachment.id);
        toast({
          title: t("toastImageAjoutSuccesTitre"),
          variant: "success",
          description: t("toastImageAjoutSuccesDescription"),
        });
      } catch (err) {
        console.error(err);
        toast({
          title: t("toastImageAjoutErreurTitre"),
          variant: "error",
          description: t("toastImageAjoutErreurDescription"),
        });
      }
      form.reset();
      URL.revokeObjectURL(imageSrc);
      navigate(`/edit/artwork/${result.id}`);
    } catch (err) {
      if (isAxiosError(err) && err.response?.status === 409) {
        form.setError("oeuvresDescriptionNumeroEnregistrement", {
          message: t("itemWithSameIdAlreadyExists"),
        });
        return;
      }
      throw err;
    }
  };

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)}>
        <div className="flex flex-col gap-4 items-center">
          <div className="p-6">
            <Title>{t("artmatch.createNewArtwork.title")}</Title>
          </div>
          <div className="flex flex-col gap-4 items-center">
            <img
              src={imageSrc}
              style={{
                maxWidth: "400px",
                maxHeight: "400px",
                objectFit: "contain",
              }}
            />
            <FormField
              control={form.control}
              name="oeuvresDescriptionNumeroEnregistrement"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    {t("oeuvresDescriptionNumeroEnregistrement")}
                    {"*"}
                  </FormLabel>
                  <FormControl>
                    <Input {...field} />
                  </FormControl>
                  {form.formState.submitCount > 0 && <FormMessage />}
                </FormItem>
              )}
            />
          </div>
          <div className="flex gap-2 p-4 border-t border-gray-200">
            <Button
              variant="secondary"
              size="lg"
              className="text-base"
              type="button"
              onClick={handleCancel}
            >
              {t("artmatch.createNewArtwork.cancel")}
            </Button>
            <Button size="lg" className="text-base" type="submit">
              {t("artmatch.createNewArtwork.create")}
            </Button>
          </div>
        </div>
      </form>
    </Form>
  );
};
